import React from 'react';
import Pagination, { usePagination } from '../../../components/ui/Pagination';
import EmptyState from '../../../components/ui/EmptyState';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import SortableTh, { useSort } from '../../../components/ui/SortableTh';
import useTableFilters from '../../../components/ui/useTableFilters';
import { formatDate } from '../../../utils/formatters';

/**
 * Submissions, at dashboard depth.
 *
 * This used to render five stacked rows per paper — a pipeline stepper, file
 * names and sizes, review tallies, a progress bar and the camera-ready upload.
 * All of that belongs on /my-submissions, which is the screen whose whole job
 * is one paper in detail; having it here left that page with nothing to do and
 * made the dashboard impossible to scan.
 *
 * So this is now a table: which paper, which conference, what step it's at, and
 * a way through to the detail. Deliberately *not* shown: how many reviews have
 * come back. An author can't act on "2 of 3 reviews in" — it's someone else's
 * progress bar. The step alone answers the only question this screen owes them,
 * which is whether anything is waiting on them.
 */

/** Where a paper sits in the pipeline, as one plain phrase. */
const STEP_KEYS = {
  draft: 'mySubmissions.steps.draft',
  submitted: 'mySubmissions.steps.submitted',
  under_review: 'mySubmissions.steps.underReview',
  revision_required: 'mySubmissions.steps.revisionRequired',
  accepted: 'mySubmissions.steps.accepted',
  rejected: 'mySubmissions.steps.rejected',
  withdrawn: 'mySubmissions.steps.withdrawn',
};

const STEP_TONE = {
  draft: 'text-muted-foreground',
  submitted: 'text-primary',
  under_review: 'text-warning',
  revision_required: 'text-accent',
  accepted: 'text-success',
  rejected: 'text-error',
  withdrawn: 'text-muted-foreground',
};

/** Statuses that mean the ball is in the author's court. */
const NEEDS_AUTHOR = new Set(['draft', 'revision_required']);

const MySubmissions = ({ submissions, loading }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');

  const { sort, toggle, sortRows } = useSort('submittedAt', 'desc');
  const f = useTableFilters(submissions, React.useMemo(() => ({
    conference: {
      label: t('mySubmissions.columns.conference', 'Conference'),
      accessor: (r) => r?.conference?.shortName || r?.conference?.name || r?.conferenceShortName || r?.conferenceName || '',
      emptyLabel: '—',
    },
    step: {
      label: t('mySubmissions.columns.step', 'Step'),
      accessor: (r) => r?.status || 'draft',
      order: ['draft','submitted','under_review','revision_required','accepted','rejected','withdrawn'],
      format: (v) => t(STEP_KEYS[v] || STEP_KEYS.draft),
    },
  }), [t]));
  const SORT_ACCESSORS = {
    conference: (r) => r?.conference?.shortName || r?.conference?.name || r?.conferenceShortName || '',
    step: (r) => t(STEP_KEYS[r?.status] || STEP_KEYS.draft),
    submittedAt: (r) => r?.submittedAt || r?.createdAt,
  };
  const rows = sortRows(f.filterRows(submissions), SORT_ACCESSORS);
  // Dashboard depth: a short page, with the full list one click away.
  const { pageRows, ...pager } = usePagination(rows, 8);

  // Opens the detail screen with this paper singled out, rather than dropping
  // the person into a list and making them find it again.
  const openDetail = (id) => navigate(`/my-submissions?focus=${id}`);

  if (loading) {
    return (
      <div className="bg-card border border-border p-10 flex items-center justify-center shadow-sm">
        <Icon name="Loader" size={20} className="animate-spin text-primary" />
      </div>
    );
  }

  if (!submissions || submissions.length === 0) {
    return (
      <div className="bg-card border border-border overflow-hidden shadow-sm">
        <div className="px-5 py-3 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground">{t('mySubmissions.title')}</h2>
        </div>
        <EmptyState
          icon="FileText"
          title={t('mySubmissions.emptyState.message')}
          body={t('mySubmissions.emptyState.description', 'Papers you submit to any conference show up here, with their review status.')}
          action={{ label: t('mySubmissions.emptyState.browseConferences'), icon: 'Search', onClick: () => navigate('/public-conference-board') }}
        />
      </div>
    );
  }

  return (
    <div className="bg-card border border-border overflow-hidden shadow-sm">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-foreground">{t('mySubmissions.title')}</h2>
          {/* Count lives with the thing it counts, not in a card of its own. */}
          <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 bg-muted text-[11px] font-semibold text-muted-foreground tabular-nums">
            {submissions.length}
          </span>
        </div>
        <Button variant="outline" size="xs" onClick={() => navigate('/my-submissions')} iconName="ArrowRight" iconPosition="right" iconSize={13}>
          {t('mySubmissions.viewAll', 'View all')}
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="table-clear">
          <thead>
            <tr>
              <SortableTh field="title" sort={sort} onSort={toggle} className="col-primary">
                {t('mySubmissions.columns.paper', 'Paper')}
              </SortableTh>
              <SortableTh field="conference" sort={sort} onSort={toggle} filter={f.headerProps('conference')}>
                {t('mySubmissions.columns.conference', 'Conference')}
              </SortableTh>
              <SortableTh field="step" sort={sort} onSort={toggle} filter={f.headerProps('step')}>
                {t('mySubmissions.columns.step', 'Step')}
              </SortableTh>
              <SortableTh field="submittedAt" sort={sort} onSort={toggle} align="end">
                {t('mySubmissions.columns.submitted', 'Submitted')}
              </SortableTh>
              <th className="col-tight" />
            </tr>
          </thead>
          <tbody>
            {pageRows?.map((s) => {
              const step = t(STEP_KEYS[s?.status] || STEP_KEYS.draft);
              const tone = STEP_TONE[s?.status] || STEP_TONE.draft;
              const waiting = NEEDS_AUTHOR.has(s?.status);
              return (
                <tr key={s?.id}>
                  <td className="col-primary">
                    <button
                      onClick={() => openDetail(s?.id)}
                      className="text-start font-medium text-foreground hover:text-primary transition-colors"
                    >
                      {s?.title}
                    </button>
                  </td>
                  <td className="text-muted-foreground">
                    {s?.conference?.shortName || s?.conference?.name || s?.conferenceShortName || s?.conferenceName}
                  </td>
                  <td>
                    <span className={`inline-flex items-center gap-1.5 font-medium ${tone}`}>
                      {/* One dot, filled only when it's the author's move. The
                          row shouldn't have to shout to say "nothing to do". */}
                      <span
                        className={`w-1.5 h-1.5 ${waiting ? 'bg-current' : 'border border-current'}`}
                        aria-hidden="true"
                      />
                      {step}
                    </span>
                  </td>
                  <td className="col-num text-muted-foreground">
                    {formatDate(s?.submittedAt || s?.createdAt)}
                  </td>
                  <td className="col-tight">
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={() => openDetail(s?.id)}
                      iconName="ArrowRight"
                      iconPosition="right"
                      iconSize={12}
                    >
                      {t('mySubmissions.buttons.details', 'Details')}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination {...pager} />
    </div>
  );
};

export default MySubmissions;
